<template>
  <div class="relative mb-4 mt-3">
    <label
      v-if="label"
      class="absolute left-4 -top-0 bg-white px-2 text-gray-400 text-xs font-semibold pointer-events-none"
      style="transform: translateY(-50%);"
    >
      {{ label }}
    </label>
    <button
      type="button"
      class="w-full rounded-full border px-5 py-3 text-base text-left focus:outline-none focus:ring-2 focus:ring-primary transition flex items-center justify-between"
      :class="[
        { 'text-gray-400': !displayValue },
        danger ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
      ]"
      @click="toggleDropdown"
      :disabled="disabled"
    >
      <span>{{ displayValue || placeholder }}</span>
      <i class="bx bx-chevron-down text-2xl text-gray-400 ml-2"></i>
    </button>
    <teleport to="body">
      <div
        v-if="open"
        ref="dropdown"
        class="absolute bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto"
        style="z-index: 9999; position: fixed;"
        :style="dropdownStyle"
      >
        <div v-if="searchEnabled" class="sticky top-0 bg-white z-10 px-3 pt-3 pb-2">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full border border-gray-200 rounded-full px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Cerca..."
          />
        </div>
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="px-5 py-3 cursor-pointer hover:bg-gray-100 text-left flex items-center"
          @click="select(option.value)"
        >
          <template v-if="multiple">
            <input
              type="checkbox"
              class="mr-2"
              :checked="Array.isArray(value) && value.includes(option.value)"
              @change.prevent
              @click.stop="select(option.value)"
            />
          </template>
          {{ option.label }}
        </div>
        <div v-if="filteredOptions.length === 0" class="px-5 py-3 text-gray-400 text-center">
          Nessun risultato
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import Teleport from 'vue2-teleport';

export default {  
  name: "CuSelect",
  props: {
    value: {
      type: [String, Number, Array],
      default: ''
    },
    label: String,
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: "Seleziona"
    },
    disabled: Boolean,
    danger: { type: Boolean, default: false },
    searchable: { type: [Boolean,Object], default: null },
    multiple: { type: Boolean, default: false }
  },
  components: {
    Teleport
  },
  data() {
    return {
      open: false,
      searchQuery: "",
      dropdownStyle: {}
    }
  },
  computed: {
    searchEnabled() {
      if (this.searchable === true) return true
      if (this.searchable === false) return false
      if (this.searchable === null) return this.options.length > 10
      return false;
    },
    displayValue() {
      if (this.multiple) {
        if (Array.isArray(this.value) && this.value.length > 0) {
          return this.options
            .filter(opt => this.value.includes(opt.value))
            .map(opt => opt.label)
            .join(', ')
        }
        return ""
      } else {
        const found = this.options.find(opt => opt.value === this.value)
        return found ? found.label : ""
      }
    },
    filteredOptions() {
      if (this.options.length > 10 && this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        return this.options.filter(opt =>
          String(opt.label).toLowerCase().includes(q)
        )
      }
      return this.options
    }
  },
  methods: {
    updateDropdownPosition() {
      if (!this.open || !this.$refs.dropdown) return
      
      const button = this.$el.querySelector('button')
      if (!button) return
      
      const rect = button.getBoundingClientRect()
      const dropdownHeight = 240 // max-h-60 in pixels
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      
      // Position dropdown below by default, above if not enough space
      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow
      
      this.dropdownStyle = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        top: showAbove ? `${rect.top - dropdownHeight}px` : `${rect.bottom + 8}px`,
        maxHeight: showAbove ? `${Math.min(dropdownHeight, spaceAbove - 10)}px` : `${Math.min(dropdownHeight, spaceBelow - 10)}px`
      }
    },
    toggleDropdown() {
      if (!this.disabled) {
        this.open = !this.open
        if (this.open) {
          this.$nextTick(() => {
            this.searchQuery = ""
            this.updateDropdownPosition()
          })
        }
      }
    },
    select(val) {
      if (this.multiple) {
        let newValue = Array.isArray(this.value) ? [...this.value] : []
        const idx = newValue.indexOf(val)
        if (idx === -1) {
          newValue.push(val)
        } else {
          newValue.splice(idx, 1)
        }
        this.$emit('input', newValue)
        // Do not close dropdown on multi-select
      } else {
        this.$emit('input', val)
        this.open = false
      }
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target) && (!this.$refs.dropdown || !this.$refs.dropdown.contains(e.target))) {
        this.open = false
      }
    },
    handleResize() {
      if (this.open) {
        this.updateDropdownPosition()
      }
    },
    handleScroll() {
      if (this.open) {
        this.updateDropdownPosition()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('scroll', this.handleScroll, true)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('scroll', this.handleScroll, true)
  }
}
</script>
